import { useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
// Tabs removed — single-panel auth UI
import { Navigation } from "@/components/ui/navigation";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandEmpty, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";

type SignInValues = { identifier: string; password: string };
type SubjectSelectionKey = "enrolledSubjects" | "plannedSubjects";

const initialSignIn: SignInValues = { identifier: "", password: "" };

export default function Auth() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { session, loading: authLoading, signIn, signInWithOAuth } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [remoteError, setRemoteError] = useState<string | null>(null);
  const isBusy = submitting || authLoading;
  // Only sign-in flow is supported (Google + legacy email/password)
  const [showLegacy, setShowLegacy] = useState(false);

  const [signInValues, setSignInValues] = useState<SignInValues>(initialSignIn);

  // Compute redirect target first (needed by handleSignInWithGoogle)
  const redirectTo = useMemo(() => {
    const state = location.state as { from?: string } | null;
    if (state?.from) return state.from;
    return "/dashboard";
  }, [location.state]);

  // Handle credential response from the GSI onload/data-callback mechanism
  const handleSignInWithGoogle = useCallback(async (response: any) => {
    if (!response || !response.credential) {
      setRemoteError("No credential returned from Google.");
      return;
    }
    setSubmitting(true);
    setRemoteError(null);
    try {
      const { data, error } = await supabase.auth.signInWithIdToken({
        provider: "google",
        token: response.credential,
      });
      if (error) {
        setRemoteError(error.message);
        return;
      }
      // Successful Google sign-in — redirect immediately (no migration needed)
      console.log("Signed in with Google via id token", data);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      console.error("Failed to sign in with Google id token", err);
      setRemoteError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }, [navigate, redirectTo]);

  const [errors, setErrors] = useState<Record<string, string>>({});
  // Clean, flat catalog of AP subjects for multi-select dropdowns
  const AP_SUBJECTS: Array<{ value: string; label: string }> = [
    // English
    { value: "english-language-and-composition", label: "AP English Language and Composition" },
    { value: "english-literature-and-composition", label: "AP English Literature and Composition" },
    // History & Social Science
    { value: "united-states-history", label: "AP United States History" },
    { value: "world-history", label: "AP World History: Modern" },
    { value: "european-history", label: "AP European History" },
    { value: "us-government-and-politics", label: "AP U.S. Government & Politics" },
    { value: "comparative-government-and-politics", label: "AP Comparative Government & Politics" },
    { value: "human-geography", label: "AP Human Geography" },
    { value: "macroeconomics", label: "AP Macroeconomics" },
    { value: "microeconomics", label: "AP Microeconomics" },
    { value: "psychology", label: "AP Psychology" },
    // Math & CS
    { value: "precalculus", label: "AP Precalculus" },
    { value: "calculus-ab", label: "AP Calculus AB" },
    { value: "calculus-bc", label: "AP Calculus BC" },
    { value: "statistics", label: "AP Statistics" },
    { value: "computer-science-a", label: "AP Computer Science A" },
    { value: "computer-science-principles", label: "AP Computer Science Principles" },
    // Sciences
    { value: "biology", label: "AP Biology" },
    { value: "chemistry", label: "AP Chemistry" },
    { value: "environmental-science", label: "AP Environmental Science" },
    { value: "physics-1", label: "AP Physics 1" },
    { value: "physics-2", label: "AP Physics 2" },
    { value: "physics-c-mechanics", label: "AP Physics C: Mechanics" },
    { value: "physics-c-electricity-and-magnetism", label: "AP Physics C: Electricity & Magnetism" },
    // World Languages & Cultures
    { value: "chinese-language-and-culture", label: "AP Chinese Language and Culture" },
    { value: "french-language-and-culture", label: "AP French Language and Culture" },
    { value: "german-language-and-culture", label: "AP German Language and Culture" },
    { value: "italian-language-and-culture", label: "AP Italian Language and Culture" },
    { value: "japanese-language-and-culture", label: "AP Japanese Language and Culture" },
    { value: "latin", label: "AP Latin" },
    { value: "spanish-language-and-culture", label: "AP Spanish Language and Culture" },
    { value: "spanish-literature-and-culture", label: "AP Spanish Literature and Culture" },
    // Arts
    { value: "art-history", label: "AP Art History" },
    { value: "music-theory", label: "AP Music Theory" },
    { value: "art-and-design-2d", label: "AP 2-D Art and Design" },
    { value: "art-and-design-3d", label: "AP 3-D Art and Design" },
    { value: "drawing", label: "AP Drawing" },
    // Capstone
    { value: "ap-seminar", label: "AP Seminar" },
    { value: "ap-research", label: "AP Research" },
  ];

  // (Signup flows removed — legacy email/password sign-in retained)

  // Check if user needs migration (signed in but no Google identity linked)
  const hasGoogleIdentity = session?.user?.identities?.some((id) => id.provider === "google") ?? false;
  const showMigrationPrompt = Boolean(session) && !hasGoogleIdentity;

  // Password is now automatically disabled server-side via database trigger
  // when a Google identity is linked (handles both manual linking and auto-linking)

  useEffect(() => {
    // Only redirect if user has a Google identity linked (no migration needed)
    if (!authLoading && session && hasGoogleIdentity) {
      navigate(redirectTo, { replace: true });
    }
  }, [authLoading, session, hasGoogleIdentity, redirectTo, navigate]);

  // no-op: removed mode URL syncing (only signin supported)

  // Expose the callback to window for the data-callback attribute to call
  useEffect(() => {
    (window as any).handleSignInWithGoogle = (resp: any) => {
      void handleSignInWithGoogle(resp);
    };
    return () => {
      try {
        delete (window as any).handleSignInWithGoogle;
      } catch (_) {}
    };
  }, [handleSignInWithGoogle]);

  // Initialize and render the GSI button when google SDK is available.
  useEffect(() => {
    const tryInit = () => {
      const googleObj = (window as any).google;
      if (!googleObj || !googleObj.accounts || !googleObj.accounts.id) return false;

      const el = document.getElementById("g_id_signin");
      const onloadEl = document.getElementById("g_id_onload");
      // Hardcoded Google client ID (intentionally not read from env)
      const clientId = "812399317646-ii7683kvqjjkp41dq4o66vseqbaenq3i.apps.googleusercontent.com";

      try {
        googleObj.accounts.id.initialize({
          client_id: clientId,
          callback: (response: any) => {
            void handleSignInWithGoogle(response);
          },
          ux_mode: onloadEl?.getAttribute("data-ux_mode") ?? "popup",
        });

        if (el) {
          googleObj.accounts.id.renderButton(el, { theme: "outline", size: "large" });
        }

        return true;
      } catch (err) {
        console.error("GSI initialize/render failed", err);
        return false;
      }
    };

    // Try immediately, then retry a few times if the script hasn't loaded yet.
    if (tryInit()) return;
    let attempts = 0;
    const id = setInterval(() => {
      attempts += 1;
      if (tryInit() || attempts >= 10) {
        clearInterval(id);
      }
    }, 300);

    return () => {
      clearInterval(id);
      try {
        const googleObj = (window as any).google;
        googleObj?.accounts?.id?.cancel?.();
      } catch (_) {}
    };
  }, [handleSignInWithGoogle]);

  useEffect(() => {
    setRemoteError(null);
  }, []);

  const title = "Welcome back";

  function validateSignIn(values: SignInValues) {
    const e: Record<string, string> = {};
    if (!values.identifier.trim()) e.identifier = "Enter your email or username.";
    if (!values.password || values.password.length < 8) e.password = "Password must be at least 8 characters.";
    return e;
  }


  // password reset / update flows removed — Google handles this

  async function onSubmitSignIn(ev: React.FormEvent) {
    ev.preventDefault();
    const e = validateSignIn(signInValues);
    setErrors(e);
    if (Object.keys(e).length === 0) {
      setSubmitting(true);
      setRemoteError(null);
      try {
        const outcome = await signIn({ email: signInValues.identifier, password: signInValues.password });
        if (outcome.error) {
          // Check if it's an invalid credentials error and add migration hint
          if (outcome.error.toLowerCase().includes("invalid") || outcome.error.toLowerCase().includes("credentials")) {
            setRemoteError(`${outcome.error}. If you've already migrated your account, please sign in with Google instead.`);
          } else {
            setRemoteError(outcome.error);
          }
          return;
        }
        // Legacy sign-in successful — migration prompt will show automatically
        // (showMigrationPrompt is computed from session.user.identities)
      } finally {
        setSubmitting(false);
      }
    }
  }

  // signup removed — legacy email/password sign-up is disabled in the UI

  return (
    <>
      <Navigation />
      <main id="main" className="bg-soft-gradient min-h-[calc(100svh-64px)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <div className="mx-auto max-w-md">
            <div className="mb-4 text-center">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="text-sm text-muted-foreground hover:underline"
              >
                ← Back
              </button>
            </div>

            <Card className="bg-white/90 backdrop-blur-sm border border-neutral-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-2xl">{title}</CardTitle>
                <CardDescription>
                  Sign in with Google or your email/username and password.
                </CardDescription>
              </CardHeader>

              <CardContent>
                {showMigrationPrompt ? (
                  <div className="mt-6 space-y-4">
                    <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                      <h3 className="font-semibold text-lg">Link your Google account</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        We've upgraded to Sign in with Google for a faster, more secure experience.
                        Link your Google account to continue using Lucen.
                      </p>
                    </div>
                    {remoteError && (
                      <p className="text-sm text-red-600" role="alert">{remoteError}</p>
                    )}
                    <Button
                      className="w-full"
                      disabled={isBusy}
                      onClick={async () => {
                        setSubmitting(true);
                        setRemoteError(null);
                        try {
                          const { error } = await supabase.auth.linkIdentity({ provider: "google" });
                          if (error) {
                            setRemoteError(error.message);
                            return;
                          }
                          // linkIdentity redirects to Google; on return Supabase will have linked the account
                          toast({ title: "Google account linked!", description: "You can now sign in with Google." });
                          navigate(redirectTo, { replace: true });
                        } catch (err) {
                          setRemoteError(err instanceof Error ? err.message : String(err));
                        } finally {
                          setSubmitting(false);
                        }
                      }}
                    >
                      {isBusy ? "Linking…" : "Link Google Account"}
                    </Button>
                  </div>
                ) : (
                <div className="mt-6 space-y-4">
                  {remoteError && (
                    <p className="text-sm text-red-600" role="alert">{remoteError}</p>
                  )}

                  <div className="flex flex-col gap-3">
                    <div>
                      <div id="g_id_onload"
                        data-client_id="812399317646-ii7683kvqjjkp41dq4o66vseqbaenq3i.apps.googleusercontent.com"
                        data-context="use"
                        data-ux_mode="popup"
                        data-callback="handleSignInWithGoogle"
                        data-itp_support="true"
                        data-use_fedcm_for_prompt="true"
                      />

                      <div className="g_id_signin"
                        data-type="standard"
                        data-shape="rectangular"
                        data-theme="outline"
                        data-text="signin_with"
                        data-size="large"
                        data-logo_alignment="left"
                        id="g_id_signin"
                      />
                    </div>

                    <p className="text-center text-sm text-muted-foreground">
                      Have an old email/password account?{' '}
                      <button
                        type="button"
                        className="text-primary hover:underline"
                        onClick={() => setShowLegacy(true)}
                      >
                        Migrate account
                      </button>
                    </p>

                    <p className="text-xs text-muted-foreground text-center px-4">
                      <strong>Note:</strong> If your Google account uses the same email as your old account, 
                      just sign in with Google above — your accounts will be linked automatically.
                    </p>
                  </div>

                  {showLegacy && (
                    <div className="mt-2 space-y-3">
                      <div className="rounded-md bg-blue-50 p-3 text-sm text-blue-800">
                        <p className="font-medium">How to migrate your account:</p>
                        <ol className="mt-1 list-decimal list-inside space-y-1 text-blue-700">
                          <li>Sign in with your email and password below</li>
                          <li>Link your Google account when prompted</li>
                          <li>From then on, use Google to sign in</li>
                        </ol>
                      </div>
                      <form noValidate onSubmit={onSubmitSignIn} className="space-y-4" aria-label="Sign in form">
                        <div className="grid gap-2">
                          <Label htmlFor="identifier">Email or username</Label>
                          <Input
                            id="identifier"
                            name="identifier"
                            autoComplete="username"
                            inputMode="email"
                            placeholder="you@example.com"
                            value={signInValues.identifier}
                            onChange={(e) => setSignInValues((prev) => ({ ...prev, identifier: e.target.value }))}
                            aria-invalid={Boolean(errors.identifier)}
                            aria-describedby={errors.identifier ? "identifier-error" : undefined}
                            required
                            disabled={isBusy}
                          />
                          {errors.identifier && (
                            <p id="identifier-error" className="text-sm text-red-600">{errors.identifier}</p>
                          )}
                        </div>

                        <div className="grid gap-2">
                          <Label htmlFor="password">Password</Label>
                          <Input
                            id="password"
                            name="password"
                            type="password"
                            autoComplete="current-password"
                            placeholder="••••••••"
                            value={signInValues.password}
                            onChange={(e) => setSignInValues((prev) => ({ ...prev, password: e.target.value }))}
                            aria-invalid={Boolean(errors.password)}
                            aria-describedby={errors.password ? "password-error" : undefined}
                            required
                            disabled={isBusy}
                          />
                          {errors.password && (
                            <p id="password-error" className="text-sm text-red-600">{errors.password}</p>
                          )}
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-xs text-muted-foreground">Use 8+ characters</span>
                        </div>

                        <Button type="submit" className="w-full" disabled={isBusy}>
                          {isBusy ? "Signing in…" : "Sign in"}
                        </Button>
                      </form>
                    </div>
                  )}
                </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </>
  );
}